import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Review } from '../types/Review';
import DenseReviewList from './DenseReviewList';
import '../styles/PaginatedReviewList.css';

interface PaginatedReviewListProps {
  reviews: Review[];
  viewMode: 'table' | 'cards' | 'compact' | 'highlights';
  onUpdate: (id: string, updates: Partial<Review>) => void;
  onDelete: (id: string) => void;
  onBulkAction?: (action: string, ids: string[]) => void;
}

const PaginatedReviewList: React.FC<PaginatedReviewListProps> = ({
  reviews,
  viewMode,
  onUpdate,
  onDelete,
  onBulkAction
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  
  // Performance mode for large datasets
  const isLargeDataset = reviews.length > 1000;
  const recommendedPageSize = isLargeDataset ? 25 : 50;
  
  useEffect(() => {
    if (isLargeDataset && pageSize > 25) {
      setPageSize(25);
    }
  }, [isLargeDataset, pageSize]);

  // Calculate pagination
  const totalPages = Math.ceil(reviews.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  
  // Get current page reviews
  const currentReviews = useMemo(() => {
    return reviews.slice(startIndex, endIndex);
  }, [reviews, startIndex, endIndex]);

  // Handle page changes with loading state
  const handlePageChange = useCallback((page: number) => {
    setLoading(true);
    setCurrentPage(page);
    
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Simulate loading for better UX
    setTimeout(() => setLoading(false), 200);
  }, []);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    const halfVisible = Math.floor(maxVisible / 2);
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= halfVisible + 1) {
        for (let i = 1; i <= maxVisible - 2; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - halfVisible) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - (maxVisible - 3); i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Quick jump to page
  const [jumpToPage, setJumpToPage] = useState('');
  
  const handleJumpToPage = () => {
    const page = parseInt(jumpToPage);
    if (page >= 1 && page <= totalPages) {
      handlePageChange(page);
      setJumpToPage('');
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      
      if (e.key === 'ArrowLeft' && currentPage > 1) {
        handlePageChange(currentPage - 1);
      } else if (e.key === 'ArrowRight' && currentPage < totalPages) {
        handlePageChange(currentPage + 1);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPage, totalPages, handlePageChange]);

  return (
    <div className="paginated-review-list">
      {/* Pagination Header */}
      <div className="pagination-header">
        <div className="pagination-info">
          <span className="review-count">
            {reviews.length.toLocaleString()} total reviews
          </span>
          <span className="page-info">
            Showing {startIndex + 1}-{Math.min(endIndex, reviews.length)} 
          </span>
          {isLargeDataset && (
            <span className="performance-mode">
              🚀 Performance mode enabled
            </span>
          )}
        </div>
        
        <div className="pagination-controls">
          <label>
            Per page:
            <select 
              value={pageSize} 
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              {!isLargeDataset && <option value="100">100</option>}
            </select>
          </label>
          
          <div className="jump-to-page">
            <input
              type="number"
              placeholder="Page"
              value={jumpToPage}
              onChange={(e) => setJumpToPage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleJumpToPage()}
              min="1"
              max={totalPages}
            />
            <button onClick={handleJumpToPage}>Go</button>
          </div>
        </div>
      </div>

      {/* Review List with Loading State */}
      <div className={`review-list-container ${loading ? 'loading' : ''}`}>
        {loading ? (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading reviews...</p>
          </div>
        ) : (
          <DenseReviewList
            reviews={currentReviews}
            viewMode={viewMode}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onBulkAction={onBulkAction}
          />
        )}
      </div>

      {/* Pagination Footer */}
      <div className="pagination-footer">
        <button
          className="page-btn prev"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ← Previous
        </button>
        
        <div className="page-numbers">
          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="ellipsis">...</span>
            ) : (
              <button
                key={page}
                className={`page-btn ${currentPage === page ? 'active' : ''}`}
                onClick={() => handlePageChange(page as number)}
              >
                {page}
              </button>
            )
          ))}
        </div>
        
        <button
          className="page-btn next"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next →
        </button>
      </div>

      {/* Performance Tips for Large Datasets */}
      {isLargeDataset && (
        <div className="performance-tips">
          <h4>💡 Performance Tips:</h4>
          <ul>
            <li>Use filters to narrow down results</li>
            <li>Export filtered data for offline analysis</li>
            <li>Consider using the search feature to find specific reviews</li>
            <li>Use keyboard arrows (← →) for quick navigation</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default PaginatedReviewList;